import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import useLabels from "../../hooks/useLabels";
import {
  deleteLot,
  getLotHistory,
  getLots,
  getOperations,
  getTanks,
} from "../../services/utils";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { removeL, setLots } from "../../context/lots/lotsSlice";
import { setTanks } from "../../context/tanks/tanksSlice";
import {
  selectActiveWinery,
  selectConfigs,
  selectLots,
  selectOperations,
  selectTanks,
  selectUsers,
} from "../../context/selectors";
import { Label } from "../FormComponents";
import {
  Button,
  BatchColumn,
  CTA,
  CellarmanColumn,
  TankColumn,
  ConfirmationModal,
  Dropdown,
  RecapTankIcon,
  Tooltip,
} from "../Generic";

import "./styles/index.scss";
import AnalysisGrid from "../Home/AnalysisGrid";
import { ROLES } from "../../constants/base";
import useAuth from "../../hooks/useAuth";
import { format } from "date-fns";
import SecondaryBar from "../SecondaryBar";
import NextOperations from "../TankDetail/NextOperations";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { setOperations } from "../../context/operations/operationsSlice";
import LotLabelTags from "../LotLabelTags";
import { DestTankDetail, TreatmentsLabel } from "../OperationsDetail/Details";
import { aggregateDrugConcentrations, flattenDrugIngredients } from "../../services/common";

const LotLineage = ({relatives = [], label = ''}) => {
  const [getLabel] = useLabels();
  const navigate = useNavigate();
  const uniqueRelativesById = Array.from(new Map(relatives?.map(item => [item?.id, item])).values());
   
  return (<div className="parents">
    <Label label={label}>
    <div className="parents_options">
      {uniqueRelativesById?.map((p, index) => {
        return (<p key={`lot-parent-${index}`} className="parents_option_wrapper">
          <Tooltip
            variant={"info"}
            html={p?.note}
            place="top"
            events={["hover"]}
          >
            <span className={`parents_option ${p?.note?.trim()?.length > 0 ? "name" : ""}`}>{p?.name || ''}</span>
          </Tooltip>
          {p?.state && <span className="parents_option state">{`(${getLabel(`lotState_${p?.state}`)})`}</span>}
          <span className="icon_wrapper" onClick={() => navigate(`/lots/${p?.id}`)}>
            <FontAwesomeIcon
              size={"xs"}
              icon={"fa-link"}
              color={"rgb(100, 36, 46)"}
              title={getLabel("goToDetail")}
            />
          </span>
        </p>)
      })}
      </div>
    </Label>
  </div>)
}

export const LotInfo = ({ lot, tankList, isDetail = true, closeAdditionalInfo = () => null, tankId = null }) => {
  const [getLabel] = useLabels();
  const navigate = useNavigate();
  const configs = useSelector(selectConfigs);
  const [labelTankId, setLabelTankId] = useState(tankList?.[0]?.id || null);
  const [drugConcentrations, setDrugConcentrations] = useState([]);
  const [isDrugConcentration, setIsDrugConcentration] = useState(true);
  const [mergedBatchVarieties, setMergedBatchVarieties] = useState([]);
  const [isMergedBatchVarieties, setIsMergedBatchVarieties] = useState(false);
  const [isAdjuvant, setIsAdjuvant] = useState(false);
  
  let wineColor = "red";
  switch (lot?.color) {
    case "WHITE":
      wineColor = "white";
      break;
    case "ROSE":
      wineColor = "rose";
      break;
    default:
      wineColor = "red";
      break;
  }

  useEffect(() => {
    const drugsComposition = lot?.concentrations;
    if (Array.isArray(drugsComposition) && drugsComposition?.length > 0) {
      const drugsCompositionByTankId = drugsComposition?.filter(c => c?.tank_id === Number(labelTankId));
      const drugIngredients = flattenDrugIngredients(drugsCompositionByTankId);
      setDrugConcentrations(aggregateDrugConcentrations(isDrugConcentration, isAdjuvant, drugIngredients));
    }
    if (!labelTankId) {
      setLabelTankId(tankList?.[0]?.tank_id);
    }
  }, [lot, isDrugConcentration, isAdjuvant, labelTankId, tankList]);

  useEffect(() => {
    if (!lot?.composition || !Array.isArray(lot?.composition)) return;
    const lotComposition = [...lot?.composition
        ?.filter(c => tankId ? c?.tank_id === tankId : true)]
        ?.map(c => tankId !== null ?  ({ ...c, percentage: c.tank_percentage }) : c)
        ?.sort((a, b) =>
          (a?.variety || "")?.localeCompare(b?.variety || "")
    );

    let finalComposition = lotComposition;
    if (isMergedBatchVarieties) {
      const mergedComp = [];
      const seen = new Map();

      for (const item of lotComposition) {
        const { variety, year, origin, quantity = 0, percentage = 0 } = item;

        // Track by variety
        if (!seen.has(variety)) {
          const data = {
            variety,
            year,
            origin,
            quantity,
            percentage,
            subKeys: new Set([`${year}|${origin}`]),
            subs: [], // to store secondary rows,
          };
          seen.set(variety, data);
          mergedComp.push(data);
        } else {
          const existing = seen.get(variety);
          existing.quantity += quantity;
          existing.percentage += percentage;

          const key = `${year}|${origin}`;
          if (!existing.subKeys.has(key)) {
            existing.subKeys.add(key);
            existing.subs.push({
              year,
              origin,
              quantity,
              hideVariety: true,
            });
          }
        }
      }

      // Adjust percentages so that the rounded total is exactly 100%
      const mainItems = mergedComp;
      let total = 0;
      for (let i = 0; i < mainItems.length; i++) {
        let rounded = Math.round(mainItems[i].percentage);
        if (i === mainItems.length - 1) {
          rounded = 100 - total;
        } else {
          total += rounded;
        }
        mainItems[i].percentage = rounded;
      }

      // Group main + secondary rows and sort by total quantity
      const grouped = [];
      for (const item of mainItems) {
        grouped.push([
          {
            variety: item.variety,
            year: item.year,
            origin: item.origin,
            quantity: item.quantity,
            percentage: item.percentage,
          },
          ...item.subs,
        ]);
      }
      // Sort groups  by total quantity descending (from main entry)
      grouped.sort((a, b) => (b[0].quantity || 0) - (a[0].quantity || 0));

      // Flatten result
      finalComposition = grouped.flat();

    } else {
      // Sort non-merged composition by quantity descending
      finalComposition = [...lotComposition]?.map(l => { return { ...l, tank_name: tankList?.find(t => t?.id === l?.tank_id)?.name || "" }})?.sort(
        (a, b) => (b.quantity || 0) - (a.quantity || 0)
      );
    }

    // Single assignment for both branches
    setMergedBatchVarieties(finalComposition);
  }, [lot, isMergedBatchVarieties]);

  const filteredDrugs = drugConcentrations?.filter((c) => {
    if (isAdjuvant) return true;
    return c?.type !== "ADJUVANT";
  });

  const getTankData = (tank) => {
    const filteredComp = (lot?.composition || [])?.filter(c => c?.tank_id === tank?.id) || [];
    return { ...tank, is_src: true, planned_quantity: 0, old_quantity: tank?.quantity, composition: filteredComp?.map(f => { return { ...f, percentage: f?.tank_percentage }}) }
  }
  
  const isSameVarietyOriginYearDifferentTank = () => {
    return mergedBatchVarieties.some((e, i) =>
      mergedBatchVarieties.some(
        (f, j) =>
          i !== j &&
          e.variety === f.variety &&
          e.origin === f.origin &&
          e.year === f.year &&
          e.tank_id !== f.tank_id
      )
    );
  }

  return (
    <div className="lot_info_container">
      <div className="panel_wrapper">
        <div className="left_panel">
          {lot?.label && lot?.label?.length > 0 && (<>
              {!isDetail && <div className={`additional_info_container`}>
              <p className="close_additional" onClick={() => closeAdditionalInfo()}>
                <FontAwesomeIcon
                  icon={"fa-arrow-left"}
                  size={"xl"}
                  className="icon"
                />{" "}
              </p>
              <h4 className="aside_title">{getLabel("lotTitlePage", { name: "" })}</h4>
              <LotLabelTags
                tags={lot?.label}
                color={lot?.color?.toLowerCase?.()}
                isIcon={true}
                isTitle={true}
                isDetail={false}
              />
              {/* <Label label={getLabel("lotLabel")}>
                {lot.label?.map((lab, index) => {
                  return (
                    <p key={`label-${index}`}>{getLabel(`lotLabel_${lab}`)}</p>
                  );
                }, "") || ""}
              </Label> */}
            </div>}
            {isDetail && <LotLabelTags
                tags={lot?.label}
                color={lot?.color?.toLowerCase?.()}
                isIcon={true}
                isTitle={true}
                isDetail={true}
              />}
            </>
          )}
          {isDetail && <h4>{getLabel("lotDetails")}</h4>}
          {!isDetail && <h5>{getLabel("lotDetails")}</h5>}
          <div className="lot_info_container_row">
            {mergedBatchVarieties && mergedBatchVarieties.length > 0 && (
              <>
                <Label
                  label={getLabel("lotVarieties")}
                  customClassName="lot_info_varieties"
                >
                  {mergedBatchVarieties.map((item, index) => (
                    <p key={`variety-${index}`}>
                      {`${item?.variety || "\u00A0"} ${
                        (isMergedBatchVarieties ||
                        mergedBatchVarieties.some((e, i) =>
                          !isSameVarietyOriginYearDifferentTank()
                        )) &&
                        item.percentage && item.percentage !== 100
                          // ? `(${Math.round(item.percentage)}%)`
                          ? `(${item.percentage}%)`
                          : ""
                      }`}
                    </p>
                  ))}
                </Label>
                
                <Label label={getLabel("lotYears")}>
                  {mergedBatchVarieties.map((item, index) => (
                    <p key={`year-${index}`}>{item.year || ""}</p>
                  ))}
                </Label>

                <Label label={getLabel("lotOrigins")}>
                  {mergedBatchVarieties.map((item, index) => (
                    <p key={`origin-${index}`}>{item.origin || ""}</p>
                  ))}
                </Label>
                {isDetail && mergedBatchVarieties?.length > 1 && (
                  <Label label={getLabel("lotPercentageQuantity")}>
                    {mergedBatchVarieties?.map((item, index) => (
                      <p key={`comp-${index}`}>
                        {item?.quantity && (!isMergedBatchVarieties || !item?.hideVariety) ? `${Math.round(item.quantity)} ${lot?.state !== 'GRAPE' ? "L" : "Kg"}` ?? "-" : '\u00A0'}
                        {!isMergedBatchVarieties &&
                          isSameVarietyOriginYearDifferentTank() && item?.tank_name && ` (${item.tank_name})`}
                      </p>
                    ))}
                  </Label>
                )}
                {isDetail &&
                  mergedBatchVarieties &&
                  (
                    (isMergedBatchVarieties && (mergedBatchVarieties?.length > 1 || !isSameVarietyOriginYearDifferentTank())) ||
                    (!isMergedBatchVarieties && isSameVarietyOriginYearDifferentTank())
                  ) && ( // checks if variety is the same but either origin or year is different
                    <div className="variety_merge_cta">
                      <Tooltip
                        variant={"info"}
                        html={getLabel(
                          isMergedBatchVarieties
                            ? "lotShowVarieties"
                            : "lotHideVarieties"
                        )}
                        place="right"
                        events={["hover"]}
                      >
                        <CTA
                          customClassName={`merge ${
                            !isMergedBatchVarieties ? "on" : "off"
                          }`}
                          onClick={() =>
                            setIsMergedBatchVarieties((prev) => !prev)
                          }
                        >
                          <span className="icon">
                            <FontAwesomeIcon icon={"fa-sort-up"} />
                          </span>
                        </CTA>
                      </Tooltip>
                    </div>
                  )}
              </>
            )}
            {lot?.doco_id && lot?.doco_date && (
              <Label label={getLabel("lotDoco")}>
                {lot?.doco_id} -{" "}
                {format(new Date(lot?.doco_date), configs.shortDateFormat)}
              </Label>
            )}
          </div>
          <div className="lot_info_container_row start">
            <Label label={getLabel("lotQuality")}>
              {getLabel(`lotQuality_${lot.quality}`)}
            </Label>
            {isDetail && (
              <Label label={getLabel("lotQuantity")}>{`${
                lot?.status === "PLANNED"
                  ? lot?.planned_quantity
                  : lot?.actual_quantity
              } ${lot?.state === "GRAPE" ? "Kg" : "L"}`}</Label>
            )}
            {lot?.state === "WINE" && [0, 1, 2].includes(lot?.batch_type) && (
              <Label label={getLabel("lotType")}>
                {getLabel(`lotType${lot?.batch_type}`)}
              </Label>
            )}
            {lot?.color && (
              <Label label={getLabel("lotColor")}>
                {getLabel(`lotColor_${lot.color}`)}
              </Label>
            )}
            {lot?.state && (
              <Label label={getLabel("lotState")}>
                {getLabel(`lotState_${lot.state}`)}
              </Label>
            )}
          </div>
          {isDetail && (lot?.parents?.length > 0 || lot?.children?.length > 0) && <div>
            <h4>{getLabel("lotGenealogy")}</h4>
            <div className="lot_info_container_row start">
    
              {isDetail && lot?.parents?.length > 0 && <LotLineage relatives={lot?.parents} label={getLabel("lotParentName")} />}
              {isDetail && lot?.children?.length > 0 && <LotLineage relatives={lot?.children}label={getLabel("lotChildName")} />}
            </div>
          </div>}
          {!isDetail && tankList?.length > 0 && (
            <h5>{getLabel("lotTanks")}</h5>
          )}

          {!["GRAPE", "BOTTLED"].includes(lot.state) && (
            <>
              {tankList?.length > 0 && isDetail && (
                <h4>
                  {getLabel(
                    lot?.status === "PLANNED"
                      ? "lotProgrammedComposition"
                      : "lotComposition"
                  )}
                </h4>
              )}
              <div
                className={`lot_info_container_row ${
                  !isDetail ? "space_evenly" : ""
                }`}
              >
                {tankList?.map((tank, index) => {
                  return (
                    <div className="tanks_wrapper" key={`tank-detail-${index}`}>
                      <DestTankDetail tank={getTankData(tank)} isCurrentStateOnly={true} isLotDetail={true} isName={false} isDrop={false} />
                      {/* <RecapTankIcon tank={tank} wineColor={wineColor} /> */}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {isDetail && (
          <div className="right_panel">
            <TreatmentsLabel tankList={tankList} drugConcentrations={drugConcentrations} isDrugConcentration={isDrugConcentration} setIsDrugConcentration={setIsDrugConcentration} isAdjuvant={isAdjuvant} setIsAdjuvant={setIsAdjuvant} labelTankId={labelTankId} setLabelTankId={setLabelTankId} filteredDrugs={filteredDrugs} />
          </div>
        )}
      </div>

      {isDetail && (
        <div className="lot_info_container_row">
          <Label
            width={400}
            height={150}
            label={getLabel("lotNotes")}
            customClassName={"notes"}
          >
            {lot?.note}
          </Label>
        </div>
      )}
    </div>
  );
};

const LotDetail = () => {
  const activeWinery = useSelector(selectActiveWinery);
  const { id } = useParams();
  const operations = useSelector(selectOperations);
  const lots = useSelector(selectLots);
  const tanks = useSelector(selectTanks);
  const users = useSelector(selectUsers);
  const configs = useSelector(selectConfigs);
  const [getLabel] = useLabels();
  const dispatch = useDispatch();
  const axiosPrivate = useAxiosPrivate();
  const [isHistory, setIsHistory] = useState(false); // solo questo serve
  const [lot, setLot] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const [tankList, setTankList] = useState([]);
  const navigate = useNavigate();
  const { auth } = useAuth();
  const role = auth?.role || ROLES.GUEST;
  const pageName = getLabel("lotTitlePage", { name: lot?.name || id });

  const columns = useMemo(() => [
    {
      Header: getLabel("operationType"),
      accessor: (row) => getLabel(`operationType${row.type}`),
    },
    {
      Header: getLabel("priority"),
      accessor: (row) => getLabel(`operationPriorityLong${row.priority}`),
    },
    {
      Header: getLabel("expiringDate"),
      accessor: (row) =>
        row.expire_date
          ? format(new Date(row.expire_date), configs.shortDateFormat)
          : "",
    },
    {
      Header: getLabel("operationSrcTank"),
      accessor: (row) =>
        row?.type === "DESTEMMING" ||
        row?.src_tanks?.find((tank) => tank?.batch_id) ? (
          <BatchColumn lots={lots} rowData={row?.src_tanks} type={row?.type} />
        ) : (
          <TankColumn rowData={row?.src_tanks} tanks={tanks} />
        ),
    },
    {
      Header: getLabel("operationDestTank"),
      accessor: (row) =>
        row?.dest_tanks?.find((tank) => tank?.batch_id) ? (
          <BatchColumn lots={lots?.dest_tanks} rowData={row} />
        ) : (
          <TankColumn rowData={row?.dest_tanks} tanks={tanks} />
        ),
    },
    //  {
    //     Header: getLabel('operationCompletionDate'),
    //     accessor: (row) => row.completion_date ? format(new Date(row.completion_date), configs.shortDateFormat) : '-'
    // }, {
    //     Header: getLabel('operationNotes'),
    //     accessor: (row) => row.notes
    // }
  ]);

  if (role === ROLES.CLIENT)
    columns.push({
      Header: getLabel("cellarman"),
      accessor: (row) => (
        <CellarmanColumn rowData={row?.cellarman_ids} users={users} />
      ),
    });

  useEffect(() => {
    if (!lots || lots.length === 0) {
      loadLots();
    }
    if (!tanks || tanks.length === 0) {
      loadTanks();
    }
    if (!operations || operations.length === 0) {
      loadOperations();
    }
  }, []);

  useEffect(() => {
    const fetchLotHistory = async () => {
      const foundLot = await getLotHistory(activeWinery.id, id, axiosPrivate);
      if (foundLot?.success && foundLot?.updated_data) {
        setLot(foundLot.updated_data);
        setIsHistory(true);
      }
    };
  
    if (lots?.length > 0) {
      const foundLot = lots.find((dbLot) => dbLot.id === Number(id));
      if (foundLot) {
        setLot(foundLot);
        setIsHistory(false);
      } else {
        // Se non lo trova tra i lotti attivi, cerca nei lotti storici
        fetchLotHistory();
      }
    } else {
      // fallback: se lots non è pronto, prova direttamente con lo storico
      fetchLotHistory();
    }
  }, [lots, id]);  

  useEffect(() => {
    const filteredSet = [];
    if (isHistory) {
      setTankList([]);
      return;
    }

    lot?.tanks?.forEach((tank) => {
      const currentTank = tanks.find((t) => t?.id === tank?.tank_id) || {};
      const bType = currentTank?.batch_type !== undefined && currentTank?.batch_type !== null
        ? currentTank.batch_type
        : currentTank?.planned_batch_type;

      const batchType = ![0, 1, 2].includes(bType) ? null : currentTank?.state === 'WINE' ? getLabel(`lotType${bType}`) : null;
      filteredSet.push({ ...currentTank, ...tank, batch_type: batchType });
    });
    
    setTankList(filteredSet);
  }, [lot]);

  const handleOpenModal = () => {
    setIsOpen(true);
  };

  const handleCloseModal = () => {
    setIsOpen(false);
  };

  const handleFormSubmit = async () => {
    await removeLot();
    handleCloseModal();
  };

  const loadLots = async () => {
    const currentLots = await getLots(activeWinery.id, axiosPrivate);
    dispatch(setLots(currentLots));
  };

  const loadTanks = async () => {
    const currentTanks = await getTanks(activeWinery.id, axiosPrivate);
    dispatch(setTanks(currentTanks));
  };

  const loadOperations = async () => {
    const loadedOperations = await getOperations(axiosPrivate);
    // TO DO prendi tutte le operazioni e poi dividi tramite i selectors quelle expired e quelle ok
    // dispatch(setOperations(loadedOperations?.filter((o) => new Date(o?.expire_date) > new Date())));
    dispatch(setOperations(loadedOperations));
  };

  const removeLot = async () => {
    const toastId = toast.loading(getLabel("toast_inProgress"), {
      type: toast.TYPE.INFO,
      position: toast.POSITION.BOTTOM_RIGHT,
      exclude: true,
    });
    const response = await deleteLot(id, axiosPrivate);
    const requestPayload = {
      ...(id && { id: Number(id) }),
    };

    toast.update(toastId, {
      render: response?.success
        ? getLabel(response?.success, { name: lot?.name || "" })
        : getLabel(response?.error),
      type:
        !response || response?.error ? toast.TYPE.ERROR : toast.TYPE.SUCCESS,
      isLoading: false,
      position: toast.POSITION.BOTTOM_RIGHT,
      autoClose: 4000,
    });

    if (response && response?.success) {
      dispatch(removeL(requestPayload));
      navigate(`/lots/`);
    }
  };

  const renderLastAnalysis = () => {
    return (
      <div className="last_analysis_wrapper">
        <div className="last_analysis_grid">
          <AnalysisGrid id={id} isLot={true} />
        </div>
        <div className="last_analysis_button">
          <NavLink to={"/programming/analysis/new"}>
            <CTA>{getLabel("programOperationANALYSIS")}</CTA>
          </NavLink>
        </div>
      </div>
    );
  };
  
  const renderNavMenu = () => {
    const navClickHandler = () => {
      navigate(isHistory ? -1 : "/lots");
    };
    return (
      <div className="primary_container_menu">
        <div className="primary_container_row">
          <Button arrowDirection="left" onClick={navClickHandler} />
          <h2>{pageName}</h2>
        </div>
        <div className="primary_container_row ctas">
          {!isHistory && <NavLink to={`/lots/${id}/update`}>
            <CTA>
              {getLabel("updateLot")} <FontAwesomeIcon icon="fa-pen" />
            </CTA>
          </NavLink>}
          <NavLink to={`/lots/${id}/tracking`}>
            <CTA>
              {getLabel("showTrackData")} <FontAwesomeIcon icon="fa-timeline" />
            </CTA>
          </NavLink>
          {/* <NavLink to={`/lots/${id}/tree`}>
            <CTA>
              {getLabel("showTreeTrackData")}{" "}
              <FontAwesomeIcon icon="fa-folder-tree" />
            </CTA>
          </NavLink> */}
          {!isHistory && <NavLink to={`/lots/${id}/duplicate`}>
            <CTA>
              {getLabel("copyLot")} <FontAwesomeIcon icon="fa-clone" />
            </CTA>
          </NavLink>}
          {!isHistory && <CTA onClick={handleOpenModal}>
            {getLabel("deleteLot")} <FontAwesomeIcon icon="fa-remove" />
          </CTA>}
          <ConfirmationModal
            isOpen={isOpen}
            onConfirm={handleFormSubmit}
            onClose={handleCloseModal}
            description={getLabel("modalDeleteLotDescription", {
              name: lot?.name || "",
            })}
          ></ConfirmationModal>
        </div>
      </div>
    );
  };

  const filterOpsByState = () => {
  // save them in the localStorage
    localStorage.setItem(
      "selectedInputLotOperation",
      JSON.stringify({ type: "lot", id: id })
    );
    
    if (lot?.state === "GRAPE") {
      return [
        {
          label: getLabel("operationTypeANALYSIS"),
          handler: () => navigate("/programming/analysis/new"),
        },
        {
          label: getLabel("operationTypeDESTEMMING"),
          handler: () => navigate("/programming/destemming/new"),
        },
      ];
    } else if (lot?.state === "MUST" || lot?.state === "WINE") {
      return [
        {
          label: getLabel("operationTypeANALYSIS"),
          handler: () => navigate("/programming/analysis/new"),
        },
        {
          label: getLabel("operationTypeCUT"),
          handler: () => navigate("/programming/cut/new"),
        },
        {
          label: getLabel("operationTypeDECANT"),
          handler: () => navigate("/programming/decant/new"),
        },
        ...(lot?.state === "MUST"
          ? [
            {
              label: getLabel("operationTypeRACKING"),
              handler: () => navigate("/programming/racking/new"),
            },
            {
              label: getLabel("operationTypeFILTERING"),
              handler: () => navigate("/programming/filtering/new"),
            },
            {
              label: getLabel("operationTypeWINEMAKING"),
              handler: () => navigate("/programming/winemaking/new"),
            }]
          : []),
        ...(lot?.state === "WINE"
          ? [
            {
              label: getLabel("operationTypeWINEMAKINGPOST"),
              handler: () => navigate("/programming/winemakingpost/new"),
            },
            {
              label: getLabel("operationTypeBOTTLING"),
              handler: () => navigate("/programming/bottling/new"),
            }
          ]
          : []),
        ];
    } else if (lot?.state === "BOTTLED") {
      return [];
    }
  };

  const renderNextOperation = () => {
    return (
      <div>
        <NextOperations id={id} type={"LOT"} />
        <div className="button-margin">
          <Dropdown
            label={getLabel("programOperation")}
            options={filterOpsByState()}
          ></Dropdown>
        </div>
      </div>
    );
  };

  return (
    <div className="primary_container">
      <SecondaryBar
        breadCrumb={[
          { label: getLabel("lotsNavLink"), link: "/lots" },
          getLabel("lotTitlePage", { name: "" }),
        ]}
      />
      {renderNavMenu()}
      <LotInfo lot={lot} tankList={tankList} isHistory={isHistory} />
      {!isHistory && renderNextOperation()}
      {!isHistory && renderLastAnalysis()}
    </div>
  );
};

export default LotDetail;
